(() => {
  'use strict'

  const LIMIT_GROUPS = {
    xbloom: {
      max: Number(window.productOrderLimits?.xbloom?.max || window.xbloomOrderLimit?.max) || 5,
      quantity: Number(window.productOrderLimits?.xbloom?.quantity || window.xbloomOrderLimit?.quantity) || 0,
      message:
        window.cartStrings?.xbloomMachineLimit ||
        'A maximum of 5 xBloom Studio machines is allowed per order, across all colors.',
      variantIds: new Set([
        43114665836680, // Midnight Black
        43114690773128, // White
        43945552740488, // Twilight
        44255068160136, // Sage Green
        46278856278152, // Sand
        46489927712904, // Blue
      ]),
    },
    brezi: {
      max: Number(window.productOrderLimits?.brezi?.max) || 5,
      quantity: Number(window.productOrderLimits?.brezi?.quantity) || 0,
      message:
        window.cartStrings?.breziMachineLimit ||
        'A maximum of 5 Brezi Cold Brew Maker machines is allowed per order, across all colors.',
      variantIds: new Set([
        46169728549000, // Black
        46169728581768, // Silver
      ]),
    },
  }
  const nativeFetch = window.fetch.bind(window)
  let addQueue = Promise.resolve()

  const requestUrl = (resource) =>
    typeof resource === 'string' ? resource : resource instanceof Request ? resource.url : String(resource)

  const isCartRoute = (url, action) =>
    new RegExp(`/cart/${action}(?:\\.js)?(?:[?#]|$)`).test(new URL(url, window.location.origin).pathname)

  const cartJsonUrl = () => {
    const cartUrl = window.routes?.cart || '/cart'
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`
  }

  const groupForVariant = (variantId) =>
    Object.keys(LIMIT_GROUPS).find((group) => LIMIT_GROUPS[group].variantIds.has(Number(variantId)))

  const restrictedQuantitiesInCart = (cart) => {
    const quantities = Object.fromEntries(Object.keys(LIMIT_GROUPS).map((group) => [group, 0]))

    for (const item of cart.items || []) {
      const group = groupForVariant(item.variant_id)
      if (group) quantities[group] += Number(item.quantity) || 0
    }

    return quantities
  }

  const refreshCartState = async () => {
    try {
      const response = await nativeFetch(cartJsonUrl(), { headers: { Accept: 'application/json' } })
      if (!response.ok) return
      const cart = await response.json()
      const quantities = restrictedQuantitiesInCart(cart)
      Object.keys(LIMIT_GROUPS).forEach((group) => {
        LIMIT_GROUPS[group].quantity = quantities[group]
      })
      document.dispatchEvent(
        new CustomEvent('product-order-limits:update', {
          detail: Object.fromEntries(
            Object.entries(LIMIT_GROUPS).map(([group, config]) => [
              group,
              { quantity: config.quantity, max: config.max },
            ]),
          ),
        }),
      )
    } catch (error) {
      console.error('Unable to refresh product order limits.', error)
    }
  }

  const additionsFromObject = (body) => {
    if (!body || typeof body !== 'object') return []
    if (Array.isArray(body.items)) return body.items
    if (body.id) return [{ id: body.id, quantity: body.quantity || 1 }]
    return []
  }

  const additionsFromFormData = (body) => {
    const directId = body.get('id')
    if (directId) return [{ id: directId, quantity: body.get('quantity') || 1 }]

    const items = new Map()
    for (const [key, value] of body.entries()) {
      const match = key.match(/^items\[(\d+)]\[(id|quantity)]$/)
      if (!match) continue
      const item = items.get(match[1]) || {}
      item[match[2]] = value
      items.set(match[1], item)
    }
    return Array.from(items.values())
  }

  const requestedLimitedQuantities = (body) => {
    let additions = []

    if (body instanceof FormData) {
      additions = additionsFromFormData(body)
    } else if (body instanceof URLSearchParams) {
      additions = additionsFromFormData(body)
    } else if (typeof body === 'string') {
      try {
        additions = additionsFromObject(JSON.parse(body))
      } catch (error) {
        additions = additionsFromFormData(new URLSearchParams(body))
      }
    } else {
      additions = additionsFromObject(body)
    }

    const quantities = Object.fromEntries(Object.keys(LIMIT_GROUPS).map((group) => [group, 0]))
    for (const item of additions) {
      const group = groupForVariant(item.id)
      if (group) quantities[group] += Math.max(1, Number(item.quantity) || 1)
    }
    return quantities
  }

  const limitResponse = (group) =>
    new Response(
      JSON.stringify({
        status: 422,
        message: 'Cart Error',
        description: LIMIT_GROUPS[group].message,
      }),
      {
        status: 422,
        headers: { 'Content-Type': 'application/json' },
      },
    )

  window.fetch = (resource, options = {}) => {
    const url = requestUrl(resource)
    const addedQuantities = isCartRoute(url, 'add')
      ? requestedLimitedQuantities(options.body)
      : Object.fromEntries(Object.keys(LIMIT_GROUPS).map((group) => [group, 0]))
    const hasLimitedProduct = Object.values(addedQuantities).some((quantity) => quantity > 0)

    if (hasLimitedProduct) {
      const queuedRequest = addQueue.then(async () => {
        await refreshCartState()
        const exceededGroup = Object.keys(LIMIT_GROUPS).find(
          (group) => LIMIT_GROUPS[group].quantity + addedQuantities[group] > LIMIT_GROUPS[group].max,
        )
        if (exceededGroup) return limitResponse(exceededGroup)

        const response = await nativeFetch(resource, options)
        if (response.ok) window.setTimeout(refreshCartState, 0)
        return response
      })

      addQueue = queuedRequest.then(
        () => undefined,
        () => undefined,
      )
      return queuedRequest
    }

    const response = nativeFetch(resource, options)
    if (isCartRoute(url, 'change') || isCartRoute(url, 'update') || isCartRoute(url, 'clear')) {
      response.then((result) => {
        if (result.ok) window.setTimeout(refreshCartState, 0)
      })
    }
    return response
  }

  const visibleGroupQuantity = (item, group) => {
    const scope = item.closest('#mini-cart, .shopify-section.cart')
    if (!scope) return LIMIT_GROUPS[group].quantity

    return Array.from(
      scope.querySelectorAll(`.cart-item[data-order-limit-group="${group}"] input[name="quantity"]`),
    ).reduce((total, input) => total + (Number(input.value) || 0), 0)
  }

  const showCartItemError = (item, group) => {
    const error = item.querySelector('.cart-item__error')
    if (error) {
      error.textContent = LIMIT_GROUPS[group].message
      error.setAttribute('role', 'alert')
    }
  }

  const showProductError = (control, group) => {
    const container = control.closest('.main-product__content, .sticky-add-to-cart') || document
    const error = container.querySelector('.main-product__error') || document.querySelector('.main-product__error')
    if (error) {
      error.textContent = LIMIT_GROUPS[group].message
      error.setAttribute('role', 'alert')
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const addButton = event.target.closest('form[data-order-limit-group] button[type="submit"]')
      const addForm = addButton?.closest('form[data-order-limit-group]')
      const addGroup = addForm?.dataset.orderLimitGroup
      if (addButton && LIMIT_GROUPS[addGroup]) {
        const quantity = Number(new FormData(addForm).get('quantity')) || 1
        if (LIMIT_GROUPS[addGroup].quantity + quantity > LIMIT_GROUPS[addGroup].max) {
          event.preventDefault()
          event.stopImmediatePropagation()
          showProductError(addForm, addGroup)
          return
        }
      }

      const plusButton = event.target.closest('button[name="plus"]')
      if (!plusButton) return

      const cartItem = plusButton.closest('.cart-item[data-order-limit-group]')
      const cartGroup = cartItem?.dataset.orderLimitGroup
      if (cartItem && LIMIT_GROUPS[cartGroup] && visibleGroupQuantity(cartItem, cartGroup) >= LIMIT_GROUPS[cartGroup].max) {
        event.preventDefault()
        event.stopImmediatePropagation()
        showCartItemError(cartItem, cartGroup)
        return
      }

      const productArea = plusButton.closest('.main-product__content, .sticky-add-to-cart')
      const form = productArea?.querySelector('form[data-order-limit-group]')
      const productGroup = form?.dataset.orderLimitGroup
      if (!LIMIT_GROUPS[productGroup]) return

      const quantityInput = plusButton.closest('.quantity')?.querySelector('input[name="quantity"]')
      const selectedQuantity = Number(quantityInput?.value) || 1
      if (LIMIT_GROUPS[productGroup].quantity + selectedQuantity >= LIMIT_GROUPS[productGroup].max) {
        event.preventDefault()
        event.stopImmediatePropagation()
        showProductError(plusButton, productGroup)
      }
    },
    true,
  )

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest('form[data-order-limit-group]')
      const group = form?.dataset.orderLimitGroup
      if (!LIMIT_GROUPS[group]) return

      const quantity = Number(new FormData(form).get('quantity')) || 1
      if (LIMIT_GROUPS[group].quantity + quantity <= LIMIT_GROUPS[group].max) return

      event.preventDefault()
      event.stopImmediatePropagation()
      showProductError(form, group)
    },
    true,
  )

  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target.closest('#checkout, [name="checkout"]')
      const exceededGroup = Object.keys(LIMIT_GROUPS).find(
        (group) => LIMIT_GROUPS[group].quantity > LIMIT_GROUPS[group].max,
      )
      if (!checkoutButton || !exceededGroup) return

      event.preventDefault()
      event.stopImmediatePropagation()
      document.querySelectorAll('#cart-errors').forEach((error) => {
        error.textContent = LIMIT_GROUPS[exceededGroup].message
        error.setAttribute('role', 'alert')
      })
    },
    true,
  )

  refreshCartState()
})()
